import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { STRIP_TONES } from './Strip';
import { cn } from '@/lib/utils';

const OFF_DAYS = [
  { v: '', l: 'No weekly off day' }, { v: '6', l: 'Sunday off' }, { v: '5', l: 'Saturday off' },
  { v: '0', l: 'Monday off' }, { v: '1', l: 'Tuesday off' }, { v: '2', l: 'Wednesday off' },
  { v: '3', l: 'Thursday off' }, { v: '4', l: 'Friday off' },
];

/**
 * "Auto-build days" (M2.1 / Task B1). Previews the plan (dry run) as the
 * options change, then creates the days on confirm. No AI, no credits —
 * the copy says so, because users assume anything automatic costs credits.
 *
 * Props: open, onOpenChange, filmId, unscheduledCount, hasAutoBuilt,
 *        onBuilt(created[]) — called with the created schedules.
 */
export default function AutoBuildDialog({ open, onOpenChange, filmId, unscheduledCount, hasAutoBuilt, onBuilt }) {
  const [perDay, setPerDay] = useState(5);
  const [startDate, setStartDate] = useState('');
  const [offDay, setOffDay] = useState('6');
  const [plan, setPlan] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [building, setBuilding] = useState(false);

  const body = () => ({
    scenes_per_day: Math.max(1, Math.min(30, parseInt(perDay, 10) || 5)),
    start_date: startDate || null,
    skip_weekday: offDay === '' ? null : parseInt(offDay, 10),
    force: hasAutoBuilt,
  });

  useEffect(() => {
    if (!open) { setPlan(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const { data } = await axios.post(
          `${API_BASE}/films/${filmId}/schedules/auto-group`,
          { ...body(), dry_run: true },
          { headers: getAuthHeaders() },
        );
        if (!cancelled) setPlan(data.plan || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Could not preview the plan');
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, perDay, startDate, offDay, filmId]);

  const build = async () => {
    setBuilding(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/films/${filmId}/schedules/auto-group`,
        body(),
        { headers: getAuthHeaders() },
      );
      onBuilt?.(data.created || []);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not build days');
    } finally {
      setBuilding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="size-4 text-primary" /> Auto-build shoot days</DialogTitle>
          <DialogDescription>
            Groups the {unscheduledCount} unscheduled {unscheduledCount === 1 ? 'scene' : 'scenes'} by location, then INT/EXT, then
            day/night, and splits them into days. Days you built by hand are never touched. No AI credits are used.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Scenes per day</Label>
            <Input type="number" min="1" max="30" value={perDay} onChange={(e) => setPerDay(e.target.value)} className="h-9" data-testid="autobuild-per-day" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Day 1 date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9" data-testid="autobuild-start" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Weekly off</Label>
            <select
              value={offDay}
              onChange={(e) => setOffDay(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              disabled={!startDate}
              data-testid="autobuild-off"
            >
              {OFF_DAYS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-muted/30 max-h-64 overflow-y-auto" data-testid="autobuild-preview">
          {previewing && !plan ? (
            <div className="p-6 text-center text-xs text-muted-foreground"><Loader2 className="size-4 animate-spin inline mr-1" /> Planning…</div>
          ) : !plan || plan.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Nothing to schedule — every scene is already on a day.</div>
          ) : (
            <ul className={cn('divide-y divide-border', previewing && 'opacity-60')}>
              {plan.map((d) => (
                <li key={d.name} className="flex items-center gap-2 px-3 py-2 text-xs">
                  <span className={cn('w-1.5 h-5 rounded-sm shrink-0', STRIP_TONES[d.colour]?.bar || STRIP_TONES.neutral.bar)} />
                  <span className="font-semibold w-36 truncate">{d.name.split(' — ')[0]}{d.start_date ? <span className="text-muted-foreground font-normal"> · {d.start_date.slice(5)}</span> : null}</span>
                  <span className="flex-1 truncate">{d.location}</span>
                  <span className="text-muted-foreground tabular-nums shrink-0">Sc {d.scene_numbers.join(', ')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={building}>Cancel</Button>
          <Button onClick={build} disabled={building || previewing || !plan || plan.length === 0} data-testid="autobuild-confirm">
            {building ? <Loader2 className="size-4 animate-spin mr-1" /> : <Wand2 className="size-4 mr-1" />}
            Build {plan?.length || 0} {plan?.length === 1 ? 'day' : 'days'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
